import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getPostMeta, getPostAlternates, getAllPosts, getCategoryTranslation, categorySlug, POST_LOCALES, type PostLocale } from '@shared/lib/posts'
import { localeCategoryRoute, localeRoot, postLocaleFromPath, POST_LOCALE_LABELS } from '@shared/lib/post-locale'
import * as s from './LanguageSwitcher.css'

const POST_PATH = /^(?:\/(?:fr|es))?\/(\d{4})\/(\d{2})\/(\d{2})\/([^/]+)\/?$/
const CATEGORY_PATH = /^(?:\/(?:fr|es))?\/category\/([^/]+)\/?$/

/** Where switching to `target` should land, or null when no translation exists. */
function switchTarget(pathname: string, current: PostLocale, target: PostLocale): string | null {
  const postMatch = pathname.match(POST_PATH)
  if (postMatch) {
    const meta = getPostMeta(postMatch[4], current)
    if (!meta) return localeRoot(target)
    return getPostAlternates(meta).find((p) => p.locale === target)?.route ?? null
  }

  const categoryMatch = pathname.match(CATEGORY_PATH)
  if (categoryMatch) {
    // Category names are translated per locale, so map through any post that
    // exists in both languages instead of reusing the slug.
    const name = getAllPosts(current).find((p) => categorySlug(p.category) === categoryMatch[1])?.category
    const translated = name && getCategoryTranslation(name, current, target)
    if (translated) return localeCategoryRoute(target, categorySlug(translated))
    return localeRoot(target)
  }

  return localeRoot(target)
}

export function LanguageSwitcher() {
  const location = useLocation()
  const locale = postLocaleFromPath(location.pathname)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => { setOpen(false) }, [location.pathname])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <div ref={rootRef} className={s.root}>
      <button
        type="button"
        className={s.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={POST_LOCALE_LABELS[locale].languages}
      >
        <svg className={s.globe} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
          <circle cx="12" cy="12" r="9.2" />
          <path d="M2.8 12h18.4" />
          <path d="M12 2.8c2.6 2.4 3.9 5.6 3.9 9.2s-1.3 6.8-3.9 9.2c-2.6-2.4-3.9-5.6-3.9-9.2s1.3-6.8 3.9-9.2Z" />
        </svg>
        {locale.toUpperCase()}
      </button>

      {open && (
        <div className={s.panel} role="menu" aria-label={POST_LOCALE_LABELS[locale].languages}>
          {POST_LOCALES.map((candidate) => {
            const labels = POST_LOCALE_LABELS[candidate]
            const active = candidate === locale
            const target = active ? location.pathname : switchTarget(location.pathname, locale, candidate)
            if (target === null) {
              return (
                <span key={candidate} className={s.itemDisabled} aria-disabled="true">
                  <span className={s.nativeName}>{labels.name}</span>
                  <span className={s.codeTag}>{candidate.toUpperCase()}</span>
                </span>
              )
            }
            return (
              <a
                key={candidate}
                href={target}
                role="menuitem"
                className={`${s.item}${active ? ` ${s.itemActive}` : ''}`}
                aria-current={active ? 'true' : undefined}
                onClick={(e) => {
                  // Switching languages is a full-document navigation on
                  // purpose: the prerendered target carries the correct
                  // JSON-LD and head, which SPA routing would leave stale.
                  setOpen(false)
                  if (active) e.preventDefault()
                }}
              >
                <span className={s.nativeName}>{labels.name}</span>
                {active ? (
                  <svg className={s.check} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                    <path d="M4.5 12.5l5 5 10-11" />
                  </svg>
                ) : (
                  <span className={s.codeTag}>{candidate.toUpperCase()}</span>
                )}
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
